import { useState, useEffect, useRef } from "react";
import { useIsMobile } from "../lib/useIsMobile";
import { NAVY } from "../styles/palette";
import { sb } from "../lib/supabase";
import { loadStripe } from "../lib/stripe";

// Phase D.6 — operator payment-method management (Settings).
// Lets an operator add or replace the card we charge for THEIR iScoopCRM plan
// (the $99/mo flat fee) — distinct from their customers' cards. Adding a card
// here unlocks the flat plan for operators who onboarded before card-collection
// existed. Flow: operator-billing "init" (SetupIntent on the platform account)
// → Stripe.js confirmCardSetup → operator-billing "update_pm".
export function OperatorPaymentCard({ org, showToast, onRefresh }) {
  const isMobile = useIsMobile();
  const [editing, setEditing]   = useState(false);
  const [busy, setBusy]         = useState(false);
  const [err, setErr]           = useState("");
  const [complete, setComplete] = useState(false);
  const stripeRef = useRef(null);
  const cardElRef = useRef(null);
  const mounted   = useRef(false);

  const hasCard = !!org?.platform_pm_id;
  const last4   = org?.platform_pm_last4;
  const brand   = org?.platform_pm_brand;

  // Mount a PLATFORM card element while editing (operator pays the platform).
  useEffect(() => {
    if (!editing || mounted.current) return;
    mounted.current = true;
    loadStripe().then(stripe => {                 // no account => platform
      stripeRef.current = stripe;
      // The card field lives in Stripe's iframe, so the global 16px input rule can't
      // reach it: under 16px iOS Safari zooms the page on focus and never zooms back.
      const card = stripe.elements().create("card", {
        style: { base: { fontFamily: "Inter, sans-serif", fontSize: isMobile ? "16px" : "14px", color: "#1a1a2e", "::placeholder": { color: "#a0aec0" } } },
      });
      card.mount("#operator-pm-element");
      card.on("change", e => { setErr(e.error?.message || ""); setComplete(e.complete); });
      cardElRef.current = card;
    }).catch(() => setErr("Could not load the secure card form. Refresh and try again."));
    return () => { if (cardElRef.current) { cardElRef.current.destroy(); cardElRef.current = null; mounted.current = false; } };
  }, [editing]); // eslint-disable-line react-hooks/exhaustive-deps

  async function save() {
    setErr("");
    if (!complete) { setErr("Enter your card details to continue."); return; }
    setBusy(true);
    try {
      const init = await sb.fn("operator-billing", { action: "init" });
      if (init.error || !init.client_secret) throw new Error(init.error || "Could not start card setup");
      const { setupIntent, error } = await stripeRef.current.confirmCardSetup(init.client_secret, {
        payment_method: { card: cardElRef.current },
      });
      if (error) { setErr(error.message); setBusy(false); return; }
      const res = await sb.fn("operator-billing", { action: "update_pm", payment_method_id: setupIntent.payment_method });
      if (res.error) throw new Error(res.error);
      showToast(`Card saved — ${res.brand ? res.brand[0].toUpperCase() + res.brand.slice(1) : "card"} ···· ${res.last4 || ""}`);
      setEditing(false); setComplete(false);
      await onRefresh();
    } catch (e) {
      setErr(e.message || "Could not save your card. Please try again.");
    }
    setBusy(false);
  }

  return (
    <div className="card">
      <div className="card-title">💳 Payment method</div>
      <div style={{ fontSize: 12, color: "#8993a4", marginBottom: 14 }}>
        The card we charge for <strong>your</strong> iScoopCRM plan (the $99/mo flat fee) — not your customers' cards.
      </div>

      {!editing && hasCard && (
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <span style={{ fontSize: 14, color: NAVY, fontWeight: 600 }}>
            {brand ? brand[0].toUpperCase() + brand.slice(1) : "Card"} ···· {last4 || "••••"}
          </span>
          <button className="btn btn-ghost" onClick={() => setEditing(true)}>Update</button>
        </div>
      )}

      {!editing && !hasCard && (
        <div>
          <div style={{ fontSize: 12, color: "#974f0c", background: "#fffae6", padding: "8px 12px", borderRadius: 6, marginBottom: 12 }}>
            No card on file. Add one to enable the flat $99/mo plan.
          </div>
          <button className="btn btn-gold" onClick={() => setEditing(true)}>Add a card →</button>
        </div>
      )}

      {editing && (
        <div>
          <div id="operator-pm-element" style={{ padding: isMobile ? "14px 12px" : 12, border: "1px solid #dfe1e6", borderRadius: 6 }} />
          {err && <div style={{ fontSize: 12, color: "#bf2600", marginTop: 8 }}>⚠️ {err}</div>}
          <div style={{ marginTop: 12, display: "flex", gap: 8, justifyContent: "flex-end" }}>
            <button className="btn btn-ghost" disabled={busy} onClick={() => { setEditing(false); setErr(""); setComplete(false); }}>Cancel</button>
            <button className="btn btn-gold" disabled={busy} onClick={save}>{busy ? "Saving…" : "Save card"}</button>
          </div>
        </div>
      )}
    </div>
  );
}
